import { useEffect, useState } from 'react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Modal } from '@/components/Modal';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { tasksService, Task, TaskStatus, TaskPriority, TaskRecurrenceFrequency } from '@/services/tasks.service';
import { Plus, ListChecks, Clock, AlertCircle, Trash2, Check } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';

type TabId = 'mine' | 'today' | 'overdue' | 'all';
type Period = 'all' | 'this_month' | 'last_month' | 'this_year' | 'last_year';

function periodRange(p: Period): { from?: string; to?: string } {
  if (p === 'all') return {};
  const now = new Date();
  if (p === 'this_month') {
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    const to = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    return { from: from.toISOString(), to: to.toISOString() };
  }
  if (p === 'last_month') {
    const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const to = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
    return { from: from.toISOString(), to: to.toISOString() };
  }
  if (p === 'this_year') {
    const from = new Date(now.getFullYear(), 0, 1);
    const to = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
    return { from: from.toISOString(), to: to.toISOString() };
  }
  // last_year
  const from = new Date(now.getFullYear() - 1, 0, 1);
  const to = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999);
  return { from: from.toISOString(), to: to.toISOString() };
}

const STATUS_LABELS: Record<TaskStatus, string> = {
  open: 'Open', in_progress: 'In Progress', done: 'Done', cancelled: 'Cancelled',
};
const PRIORITY_COLORS: Record<TaskPriority, string> = {
  low:    'bg-surface-alt text-text-muted',
  normal: 'bg-info-soft text-info',
  high:   'bg-warning-soft text-warning',
  urgent: 'bg-danger-soft text-danger',
};

export default function TasksPage() {
  const [tab, setTab] = useState<TabId>('mine');
  const [period, setPeriod] = useState<Period>('all');
  const [tasks, setTasks] = useState<Task[] | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Task | null>(null);
  const [busy, setBusy] = useState(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('normal');
  const [dueAt, setDueAt] = useState('');
  const [reminderMinutesBefore, setReminderMinutesBefore] = useState<number | ''>('');
  const [recurFreq, setRecurFreq] = useState<'' | TaskRecurrenceFrequency>('');
  const [recurInterval, setRecurInterval] = useState<number>(1);

  const refresh = async () => {
    setTasks(null);
    try {
      const data = tab === 'mine'
        ? await tasksService.mine()
        : tab === 'today'
          ? await tasksService.dueToday(true)
          : tab === 'overdue'
            ? await tasksService.overdue(true)
            : await tasksService.list({ status: 'open,in_progress', ...periodRange(period) });
      setTasks(data);
    } catch { setTasks([]); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [tab, period]);

  const openNew = () => {
    setEditing(null);
    setTitle(''); setDescription(''); setPriority('normal'); setDueAt('');
    setReminderMinutesBefore('');
    setRecurFreq(''); setRecurInterval(1);
    setModalOpen(true);
  };

  const openEdit = (t: Task) => {
    setEditing(t);
    setTitle(t.title);
    setDescription(t.description ?? '');
    setPriority(t.priority);
    setDueAt(t.dueAt ? t.dueAt.slice(0, 16) : '');
    setReminderMinutesBefore(t.reminderMinutesBefore ?? '');
    setRecurFreq(t.recurrence?.frequency ?? '');
    setRecurInterval(t.recurrence?.interval ?? 1);
    setModalOpen(true);
  };

  const save = async () => {
    if (!title.trim()) { toast.error('Title required'); return; }
    setBusy(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim() || undefined,
        priority,
        dueAt: dueAt ? new Date(dueAt).toISOString() : undefined,
        reminderMinutesBefore: reminderMinutesBefore === '' ? undefined : Number(reminderMinutesBefore),
        recurrence: recurFreq ? { frequency: recurFreq, interval: Math.max(1, recurInterval) } : undefined,
      };
      if (editing) {
        await tasksService.update(editing.taskUid, payload);
        toast.success('Task updated');
      } else {
        await tasksService.create(payload);
        toast.success('Task created');
      }
      setModalOpen(false);
      await refresh();
    } catch (e: any) {
      // axios interceptor already toasted backend message
    } finally { setBusy(false); }
  };

  const toggleDone = async (t: Task) => {
    try {
      await tasksService.update(t.taskUid, { status: t.status === 'done' ? 'open' : 'done' });
      await refresh();
    } catch { toast.error('Failed to toggle'); }
  };

  const remove = async () => {
    if (!editing) return;
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(`Delete "${editing.title}"?`)) return;
    setBusy(true);
    try {
      await tasksService.remove(editing.taskUid);
      toast.success('Task removed');
      setModalOpen(false);
      await refresh();
    } finally { setBusy(false); }
  };

  return (
    <>
      <Topbar
        title="Tasks"
        breadcrumb="Workspace"
        description="Your action items across deals, contacts, and leads."
        right={<Button variant="primary" onClick={openNew}><Plus size={14} /> New task</Button>}
      />

      <div className="w-full px-7 py-7 space-y-4">
        <div className="flex flex-wrap items-center gap-1.5">
          {([
            { id: 'mine', label: 'My open', icon: <ListChecks size={12} /> },
            { id: 'today', label: 'Due today', icon: <Clock size={12} /> },
            { id: 'overdue', label: 'Overdue', icon: <AlertCircle size={12} /> },
            { id: 'all', label: 'All open' },
          ] as Array<{ id: TabId; label: string; icon?: React.ReactNode }>).map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={clsx(
                'inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-semibold transition',
                tab === t.id ? 'border-primary bg-primary-soft text-primary' : 'border-border bg-surface text-text-secondary hover:bg-surface-alt',
              )}
            >
              {t.icon}{t.label}
            </button>
          ))}
          {tab === 'all' && (
            <Select
              value={period}
              onChange={(e) => setPeriod(e.target.value as Period)}
              className="ml-auto rounded-full border border-border bg-surface px-3 py-1.5 text-xs font-semibold text-text-secondary focus:outline-none focus:border-primary"
              title="Filter by due-date range"
            >
              <option value="all">All time</option>
              <option value="this_month">This month</option>
              <option value="last_month">Last month</option>
              <option value="this_year">This year</option>
              <option value="last_year">Last year</option>
            </Select>
          )}
        </div>

        {tasks === null && <Card><div className="skeleton h-24 rounded-lg" /></Card>}

        {tasks && tasks.length === 0 && (
          <Card className="py-12 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-soft text-primary shadow-soft">
              <ListChecks size={20} />
            </div>
            <p className="text-sm font-semibold text-text-primary">
              {tab === 'mine' ? 'Nothing on your list'
                : tab === 'today' ? 'Nothing due today'
                : tab === 'overdue' ? 'No overdue tasks — nice'
                : 'No tasks yet'}
            </p>
            <p className="mx-auto mt-1 max-w-md text-xs text-text-muted">
              Tasks track your follow-ups — call this lead, send a quote, schedule a viewing. They show up here
              and on linked contact / deal pages.
            </p>
            <div className="mt-4">
              <Button onClick={openNew}><Plus size={14} /> New task</Button>
            </div>
          </Card>
        )}

        {tasks && tasks.length > 0 && (
          <Card padded={false}>
            <div className="divide-y divide-border">
              {tasks.map((t) => {
                const overdue = t.dueAt && new Date(t.dueAt).getTime() < Date.now() && t.status !== 'done';
                return (
                  <div key={t.taskUid} className="flex items-center gap-3 px-5 py-3 hover:bg-surface-alt">
                    <button
                      onClick={() => toggleDone(t)}
                      className={clsx(
                        'flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors',
                        t.status === 'done'
                          ? 'border-success bg-success text-on-primary'
                          : 'border-border hover:border-primary',
                      )}
                      title={t.status === 'done' ? 'Mark open' : 'Mark done'}
                    >
                      {t.status === 'done' && <Check size={11} />}
                    </button>
                    <div className="flex-1 cursor-pointer min-w-0" onClick={() => openEdit(t)}>
                      <div className="flex items-center gap-2">
                        <span className={clsx('truncate text-sm font-medium', t.status === 'done' && 'line-through text-text-muted')}>
                          {t.title}
                        </span>
                        <span className={clsx('rounded-full px-1.5 py-0.5 text-2xs font-semibold capitalize', PRIORITY_COLORS[t.priority])}>
                          {t.priority}
                        </span>
                        {t.status !== 'open' && t.status !== 'done' && (
                          <span className="rounded-full bg-info-soft px-1.5 py-0.5 text-2xs text-info">{STATUS_LABELS[t.status]}</span>
                        )}
                      </div>
                      <div className="mt-0.5 text-xs text-text-muted">
                        {t.dueAt ? (
                          <span className={overdue ? 'text-danger font-semibold' : ''}>
                            Due {new Date(t.dueAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        ) : 'No due date'}
                        {t.entityType && t.entityUid && <span> · linked to {t.entityType}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}
      </div>

      <Modal open={modalOpen} onClose={() => !busy && setModalOpen(false)} title={editing ? 'Edit task' : 'New task'} widthClass="max-w-lg">
        <div className="px-6 py-5">
          <FormField label="Title" required>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Follow up with…" />
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Priority">
              <Select value={priority} onChange={(e) => setPriority(e.target.value as TaskPriority)}>
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </Select>
            </FormField>
            <FormField label="Due">
              <Input type="datetime-local" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
            </FormField>
          </div>
          <FormField label="Notes">
            <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Remind me" hint="Minutes before due">
              <Select
                value={reminderMinutesBefore === '' ? '' : String(reminderMinutesBefore)}
                onChange={(e) => setReminderMinutesBefore(e.target.value === '' ? '' : Number(e.target.value))}
              >
                <option value="">No reminder</option>
                <option value="0">At due time</option>
                <option value="15">15 minutes before</option>
                <option value="60">1 hour before</option>
                <option value="240">4 hours before</option>
                <option value="1440">1 day before</option>
              </Select>
            </FormField>
            <FormField label="Repeat">
              <Select
                value={recurFreq}
                onChange={(e) => setRecurFreq(e.target.value as '' | TaskRecurrenceFrequency)}
              >
                <option value="">Doesn't repeat</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </Select>
            </FormField>
          </div>
          {recurFreq && (
            <FormField label={`Every N ${recurFreq === 'daily' ? 'days' : recurFreq === 'weekly' ? 'weeks' : recurFreq === 'monthly' ? 'months' : 'years'}`}>
              <Input
                type="number"
                min={1}
                value={recurInterval}
                onChange={(e) => setRecurInterval(Math.max(1, Number(e.target.value || 1)))}
                className="w-24"
              />
            </FormField>
          )}
        </div>
        <div className="flex items-center justify-between border-t border-border px-6 py-4">
          {editing ? <Button variant="ghost" onClick={remove} disabled={busy}><Trash2 size={14} /> Delete</Button> : <span />}
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setModalOpen(false)} disabled={busy}>Cancel</Button>
            <Button variant="primary" onClick={save} loading={busy} disabled={busy || !title.trim()}>{editing ? 'Save' : 'Create'}</Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
